/**
 * CPU mirror of `surfaceMembership` in `pointcloud.vs`.
 *
 * Exact IJK in the grown set is always a member (grow already decided;
 * stored occupancy normals on sparse cells can be garbage). Occupied shell
 * voxels that look perpendicular (`|n·plane|` below SHELL_ORTHO_MAX_ABS_DOT)
 * are hard blockers. Same-sheet shell cells with noisy PCA, and empty LOD /
 * hollow cells, snap onto an in-plane grown neighbour (not along the normal,
 * so window reveals stay out).
 */

/** Must match `SURFACE_SHELL_ORTHO_MAX_ABS_DOT` in `pointcloud.vs`. */
export const SHELL_ORTHO_MAX_ABS_DOT = 0.35;

/** Must match `SURFACE_SNAP_CHEB` in `pointcloud.vs`. */
export const SNAP_CHEB = 2;

export function offsetPointIjk(offsetP, cell, ijkOrigin) {
  return [
    Math.floor(offsetP[0] / cell) - (ijkOrigin[0] | 0),
    Math.floor(offsetP[1] / cell) - (ijkOrigin[1] | 0),
    Math.floor(offsetP[2] / cell) - (ijkOrigin[2] | 0),
  ];
}

export function ijkKey(ijk) {
  return `${ijk[0]},${ijk[1]},${ijk[2]}`;
}

export function residualToPlane(offsetP, origin, normal) {
  return (
    (offsetP[0] - origin[0]) * normal[0] +
    (offsetP[1] - origin[1]) * normal[1] +
    (offsetP[2] - origin[2]) * normal[2]
  );
}

function ndotOf(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function passesAngle(foundN, planeN, residual, cell, angleDeg) {
  const ndot = ndotOf(foundN, planeN);
  const cosAngle = Math.cos((angleDeg * Math.PI) / 180);
  return ndot >= cosAngle || (ndot <= -cosAngle && Math.abs(residual) <= 0.5 * cell);
}

function stepInPlane(di, dj, dk, planeN) {
  const len = Math.hypot(di, dj, dk);
  if (len < 1e-8) return false;
  return Math.abs((di * planeN[0] + dj * planeN[1] + dk * planeN[2]) / len) <= 0.5;
}

function firstInPlaneMemberNeighbor(gi, memberNormals, planeN) {
  for (let cheb = 1; cheb <= SNAP_CHEB; cheb++) {
    for (let di = -cheb; di <= cheb; di++) {
      for (let dj = -cheb; dj <= cheb; dj++) {
        for (let dk = -cheb; dk <= cheb; dk++) {
          if (Math.max(Math.abs(di), Math.abs(dj), Math.abs(dk)) !== cheb) continue;
          if (!stepInPlane(di, dj, dk, planeN)) continue;
          const n = memberNormals.get(ijkKey([gi[0] + di, gi[1] + dj, gi[2] + dk]));
          if (n) return { offset: [di, dj, dk], normal: n };
        }
      }
    }
  }
  return null;
}

function firstMemberNeighbor(gi, memberNormals) {
  for (let di = -1; di <= 1; di++) {
    for (let dj = -1; dj <= 1; dj++) {
      for (let dk = -1; dk <= 1; dk++) {
        if (di === 0 && dj === 0 && dk === 0) continue;
        const n = memberNormals.get(ijkKey([gi[0] + di, gi[1] + dj, gi[2] + dk]));
        if (n) return { offset: [di, dj, dk], normal: n };
      }
    }
  }
  return null;
}

/**
 * @param {object} args
 * @param {Map<string, [number, number, number]>} args.memberNormals
 * @param {Map<string, [number, number, number]>} [args.shellNormals]
 */
export function surfaceMembership(args) {
  const cell = args.cell;
  const planeN = args.planeNormal;
  if (!(cell > 1e-8)) return { member: false, residual: 0, exact: false, gi: [0, 0, 0] };
  const gi = offsetPointIjk(args.offsetP, cell, args.ijkOrigin);
  const residual = residualToPlane(args.offsetP, args.planeOrigin, planeN);
  const shellNormals = args.shellNormals || new Map();
  const memberN = args.memberNormals.get(ijkKey(gi));
  if (memberN) {
    return { member: true, residual, exact: true, gi, ndot: ndotOf(memberN, planeN) };
  }
  if (Math.abs(residual) > args.maxDistance + 0.5 * cell) {
    return { member: false, residual, exact: false, gi };
  }
  const shellN = shellNormals.get(ijkKey(gi));
  if (shellN) {
    const ndot = ndotOf(shellN, planeN);
    if (passesAngle(shellN, planeN, residual, cell, args.angleDeg)) {
      return { member: true, residual, exact: true, gi, ndot };
    }
    if (Math.abs(ndot) < SHELL_ORTHO_MAX_ABS_DOT) {
      return { member: false, residual, exact: true, gi, ndot };
    }
  }
  const snap = firstInPlaneMemberNeighbor(gi, args.memberNormals, planeN);
  if (snap) {
    return { member: true, residual, exact: false, gi, snapOff: snap.offset };
  }
  return { member: false, residual, exact: false, gi };
}

/** @deprecated test helper — first 26-neighbour in the grown set. */
export function firstGrownNeighbor(gi, memberNormals) {
  return firstMemberNeighbor(gi, memberNormals);
}
